'use client'
import { closeModal } from "@/src/store/slices/allModalSlice";
import { RootState } from "@/src/store/store";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ImageComponent from "../../ImageComponent";
import SignIn from "./SignIn";
import SignUp from "./SignUp";
import { GoBackSVG } from "@/src/ui/AllSVG";
import ForgotPassword from "./ForgotPassword";
import ForgotVerifyOtp from "./ForgotVerifyOtp";
import ResetPassword from "./ResetPassword";
import SignInEmailStep from "./SignInEmailStep";
import VerifyEmailOtpModal, { type VerifyEmailOtpSource } from "./VerifyEmailOtpModal";

// Add a new screen here and pass `navigate` to the component — that's all it takes.
export type AuthView = 'SignIn' | 'SignInEmailStep' | 'SignUp' | 'ForgotPassword' | 'ForgotVerifyOtp' | 'ResetPassword'

// Views that show a back arrow instead of the logo CTA
const BACK_VIEWS: AuthView[] = ['ForgotPassword', 'ForgotVerifyOtp', 'ResetPassword']

const BACK_DESTINATION: Partial<Record<AuthView, AuthView>> = {
    ForgotPassword: 'SignIn',
    ForgotVerifyOtp: 'ForgotPassword',
    ResetPassword: 'SignIn',
}

const LoginSignupIndex = () => {
    const dispatch = useDispatch();
    const { data } = useSelector((state: RootState) => state.allCommonModal);
    const [view, setView] = useState<AuthView>(data?.componentName)
    const [otpOverlay, setOtpOverlay] = useState<{ email: string; source: VerifyEmailOtpSource } | null>(null)
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
    const [resetToken, setResetToken] = useState('')
    const logoUrl = "/images/main/logo.svg";
    const marketplaceName = "Vallote";

    const navigate = (next: AuthView) => setView(next)
    const showOtpOverlay = (email: string, source: VerifyEmailOtpSource) =>
        setOtpOverlay({ email, source })
    const closeOtpOverlay = () => setOtpOverlay(null)

    const renderComponent = () => {
        switch (view) {
            case 'SignIn':
                return <SignIn navigate={navigate} onVerifyEmail={showOtpOverlay} />
            case 'SignInEmailStep':
                return <SignInEmailStep navigate={navigate} onVerifyEmail={showOtpOverlay} />
            case 'SignUp':
                return <SignUp navigate={navigate} onVerifyEmail={showOtpOverlay}  />
            case 'ForgotPassword':
                return (
                    <ForgotPassword
                        navigate={navigate}
                        onResetCodeSent={(email) => setForgotPasswordEmail(email)}
                    />
                )
            case 'ForgotVerifyOtp':
                return <ForgotVerifyOtp navigate={navigate} email={forgotPasswordEmail} onTokenReceived={setResetToken} />
            case 'ResetPassword':
                return <ResetPassword navigate={navigate} token={resetToken} />
            default:
                return <SignIn navigate={navigate} onVerifyEmail={showOtpOverlay} />
        }
    }

    return (
        <>
            <div className="relative grid grid-cols-1 md:grid-cols-2 h-full">
                {otpOverlay && (
                    <VerifyEmailOtpModal
                        email={otpOverlay.email}
                        source={otpOverlay.source}
                        onClose={closeOtpOverlay}
                        onVerifiedSuccess={() => dispatch(closeModal())}
                    />
                )}
                <div className="flex flex-col h-full items-start justify-between max-h-[95svh] overflow-y-auto p-12 pb-0">
                    <div className="flex flex-col gap-4">
                        <button
                            type="button"
                            onClick={() => dispatch(closeModal())}
                            className="h-12 w-50 invert shrink-0 inline-flex items-center justify-start cursor-pointer hover:opacity-80 transition-opacity"
                            aria-label="Close and go to Ask Service"
                        >
                            <ImageComponent src={logoUrl} alt={`${marketplaceName} logo`} object_cover={false} />
                        </button>
                        {BACK_VIEWS.includes(view) && (
                            <button
                                type="button"
                                onClick={() => navigate(BACK_DESTINATION[view] ?? 'SignIn')}
                                className="flex items-center gap-2 text-sm font-medium text-body-color hover:opacity-70 transition-opacity w-fit"
                                aria-label="Go back"
                            >
                                <span className="h-8 w-8 flex items-center justify-center rounded-full border border-muted-color">
                                    <GoBackSVG />
                                </span>
                                GO BACK
                            </button>
                        )}
                    </div>

                    {
                        renderComponent()
                    }
                </div>
                <div
                    className="hidden md:block relative flex-1 h-25 md:h-full overflow-hidden rounded-tl-[40px] rounded-bl-[40px] bg-[#F2F3F4]  bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url(/images/bg/auth.png)" }}
                >
                    <div className="absolute inset-0 flex flex-col justify-end p-5">
                    </div>
                </div>
            </div>
        </>
    )
}

export default LoginSignupIndex